/**
 * Line chart — total score trend over time, one line per site.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { DashboardObservation } from '@/lib/dashboard/types';

interface TrendRow {
  date: string;
  [site: string]: string | number | null;
}

/** Build per-date objects with one key per site. */
function buildSeriesData(observations: DashboardObservation[]) {
  const sites = [...new Set(observations.map((o) => o.siteName))].sort();
  const byDate = new Map<string, TrendRow>();

  for (const o of observations) {
    if (!o.observationDate || o.totalAverage === null) continue;
    const row = byDate.get(o.observationDate) ?? { date: o.observationDate };
    row[o.siteName] = o.totalAverage;
    byDate.set(o.observationDate, row);
  }

  const data = Array.from(byDate.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  );

  return { sites, data };
}

const SITE_COLORS = ['#3a86c8', '#e36a4d', '#c6a24a', '#8fbf3f', '#1f2b5b', '#7c5cbf', '#9f1239', '#0d9488'];

export function TrendLineChart({ observations }: { observations: DashboardObservation[] }) {
  const { sites, data } = buildSeriesData(observations);

  if (data.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-5 text-center text-sm text-muted-foreground">
        Not enough data for trend analysis.
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="mb-4 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        Total Score Trend by Site
      </h3>
      <ResponsiveContainer width="100%" height={380}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis domain={[0, 7]} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine
            y={5}
            stroke="#8AB74A"
            strokeDasharray="4 4"
            label={{ value: 'Good', fill: '#8AB74A', fontSize: 11 }}
          />
          {sites.map((site, i) => (
            <Line
              key={site}
              type="monotone"
              dataKey={site}
              stroke={SITE_COLORS[i % SITE_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
